import { useState, useEffect, useCallback } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import {
  userFeedbackManager,
  buildMailtoUrl,
  type FeedbackType,
  type FeedbackPriority,
  type UserFeedback,
} from '@/utils/userFeedback';

const FEEDBACK_TYPES: { value: FeedbackType; label: string }[] = [
  { value: 'bug_report', label: 'Bug Report' },
  { value: 'feature_request', label: 'Feature Request' },
  { value: 'general_feedback', label: 'General Feedback' },
  { value: 'usability_issue', label: 'Usability Issue' },
  { value: 'performance_issue', label: 'Performance Issue' },
];

const CATEGORIES: { value: string; label: string }[] = [
  { value: 'general', label: 'General' },
  { value: 'calculator', label: 'Calculator' },
  { value: 'analysis', label: 'Analysis' },
  { value: 'charts', label: 'Charts' },
  { value: 'studies', label: 'Studies' },
  { value: 'reference', label: 'Reference' },
  { value: 'settings', label: 'Settings' },
  { value: 'data_entry', label: 'Data Entry' },
  { value: 'bug', label: 'Bug' },
  { value: 'feature_request', label: 'Feature Request' },
  { value: 'accessibility', label: 'Accessibility' },
];

const PRIORITIES: { value: FeedbackPriority; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

interface FeedbackFormProps {
  isOpen: boolean;
  onClose: () => void;
  initialType?: FeedbackType;
  initialCategory?: string;
  initialError?: string;
}

export function FeedbackForm({
  isOpen,
  onClose,
  initialType = 'general_feedback',
  initialCategory = 'general',
  initialError,
}: FeedbackFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [feedbackId, setFeedbackId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    type: initialType,
    title: initialError ? `Error: ${initialError.substring(0, 50)}...` : '',
    description: initialError || '',
    category: initialCategory,
    priority: 'medium' as FeedbackPriority,
  });

  useEffect(() => {
    if (isOpen) {
      setFormData({
        type: initialType,
        title: initialError ? `Error: ${initialError.substring(0, 50)}...` : '',
        description: initialError || '',
        category: initialCategory,
        priority: 'medium',
      });
      setSubmitted(false);
      setFeedbackId(null);
    }
  }, [isOpen, initialType, initialCategory, initialError]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setIsSubmitting(true);
      try {
        const payload = { ...formData, url: window.location.href };
        const id = userFeedbackManager.submitFeedback(payload);
        setFeedbackId(id);

        const mailtoUrl = buildMailtoUrl(payload);
        if (mailtoUrl) window.location.href = mailtoUrl;

        setSubmitted(true);
      } catch (err) {
        console.error('Feedback submission failed:', err);
      } finally {
        setIsSubmitting(false);
      }
    },
    [formData],
  );

  const handleChange = (field: string, value: string) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  return (
    <Modal
      open={isOpen}
      title="Send Feedback"
      onClose={onClose}
      footer={
        submitted ? null : (
          <>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={isSubmitting}
              disabled={!formData.title.trim() || !formData.description.trim()}
              onClick={(e) => handleSubmit(e as unknown as React.FormEvent)}
            >
              {isSubmitting ? 'Opening mail…' : 'Send Feedback'}
            </Button>
          </>
        )
      }
    >
      {submitted ? (
        <div className="feedback-success">
          <p className="feedback-success-title">Thank you!</p>
          <p className="feedback-success-body">
            Your mail client should have opened. Send the pre-filled email to
            complete your submission.
          </p>
          {feedbackId ? (
            <p className="feedback-success-ref">Ref: {feedbackId}</p>
          ) : null}
          <Button variant="primary" onClick={onClose}>
            Close
          </Button>
        </div>
      ) : (
        <form className="feedback-form" onSubmit={handleSubmit}>
          <div className="select-wrapper">
            <label className="field-label" htmlFor="fb-type">
              Type
            </label>
            <select
              id="fb-type"
              className="select"
              value={formData.type}
              onChange={(e) => handleChange('type', e.target.value)}
            >
              {FEEDBACK_TYPES.map((ft) => (
                <option key={ft.value} value={ft.value}>
                  {ft.label}
                </option>
              ))}
            </select>
          </div>

          <div className="select-wrapper">
            <label className="field-label" htmlFor="fb-category">
              Category
            </label>
            <select
              id="fb-category"
              className="select"
              value={formData.category}
              onChange={(e) => handleChange('category', e.target.value)}
            >
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>

          <div className="feedback-field">
            <p className="field-label">Priority</p>
            <div className="mode-toggle feedback-priority-toggle">
              {PRIORITIES.map((p) => (
                <button
                  key={p.value}
                  type="button"
                  className={
                    formData.priority === p.value ? 'active' : ''
                  }
                  onClick={() => handleChange('priority', p.value)}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div className="text-input-wrapper">
            <label className="field-label" htmlFor="fb-title">
              Title *
            </label>
            <input
              id="fb-title"
              type="text"
              className="text-input"
              required
              aria-required="true"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="Brief summary"
            />
          </div>

          <div className="feedback-field">
            <label className="field-label" htmlFor="fb-description">
              Description *
            </label>
            <textarea
              id="fb-description"
              className="feedback-textarea"
              rows={5}
              required
              aria-required="true"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Please describe in detail..."
            />
          </div>
        </form>
      )}
    </Modal>
  );
}

interface FeedbackButtonProps {
  onOpen: (
    type?: FeedbackType,
    category?: string,
    error?: string,
  ) => void;
}

export function FeedbackButton({ onOpen }: FeedbackButtonProps) {
  return (
    <button
      type="button"
      className="feedback-fab"
      onClick={() => onOpen()}
      aria-label="Send feedback"
      title="Send feedback"
    >
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
    </button>
  );
}

export type { UserFeedback, FeedbackType, FeedbackPriority };
